import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { AppCard } from '../../components/ui/AppCard';
import { AvatarView } from '../../components/ui/AvatarView';
import { useInfiniteScroll } from '../../lib/useInfiniteScroll';
import { searchChannels, fetchChannel } from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import type { Channel } from '../../types';

const PAGE_SIZE = 30;

// Remembers the last visit's results/scroll position across a full
// unmount/remount - React Router unmounts this screen entirely when a
// card is tapped (navigating into ChatScreen), so component state alone
// can't survive a "back" into Showcase. A plain module-level variable
// (not React state, not sessionStorage) is enough: it lives as long as
// the page's JS does, which covers both a real browser-history back
// (ChatScreen's back arrow now does `navigate(-1)` - see its own comment)
// and re-clicking the Showcase tab. Only the most recent visit is kept,
// not a full stack - good enough for "I tapped one channel and came
// back", not for deep multi-hop navigation.
interface ShowcaseCache {
  query: string;
  results: Channel[];
  hasMore: boolean;
  scrollY: number;
}
let showcaseCache: ShowcaseCache | null = null;

// Server-side channel discovery, public channels only. An empty query
// returns the top searchable PUBLIC channels ranked best-first (backend/
// workflows/channel/search.wsl's rank path); a typed query switches to a
// debounced prefix search. Every result is shown as-is - including
// channels the caller already owns / joined / follows: this is "browse
// what's out there", and membership is not a reason to hide one. Mirrors
// TemplatePickerScreen's debounce/race-guard pattern.
//
// Paginated with the same sentinel/IntersectionObserver pattern as
// ChannelsListScreen/TemplatesListScreen (see useInfiniteScroll) - a new
// query resets to page 0, scrolling near the bottom appends the next
// page via searchChannels' offset/limit.
export function ShowcaseScreen() {
  const { searchQuery, setSearchQuery } = useStore();
  // Only reuse the cache if it was left by the exact same query - a
  // restored scroll position for a different search wouldn't line up
  // with anything on screen.
  const restored = showcaseCache?.query === searchQuery ? showcaseCache : null;
  const [publicResults, setPublicResults] = useState<Channel[]>(restored?.results ?? []);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(restored?.hasMore ?? true);
  const requestId = useRef(0);
  // Consumed exactly once, right after a restored mount, to skip the
  // fetch effect's normal "run on mount" pass - the results are already
  // there, refetching would just flash a loading state over them.
  const skipInitialFetchRef = useRef(restored !== null);

  useEffect(() => {
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    const thisRequest = ++requestId.current;
    const timer = setTimeout(
      async () => {
        setLoading(true);
        try {
          const page = await queryClient.query({
            queryKey: queryKeys.channelSearch(searchQuery, 0),
            queryFn: () => searchChannels(searchQuery, 0, PAGE_SIZE),
          });
          const hydrated = (await Promise.all(page.data.map((n) => fetchChannel(n)))).filter(
            (c): c is Channel => c !== null
          );
          if (requestId.current === thisRequest) {
            setPublicResults(hydrated);
            setHasMore(page.meta.pagination.hasMore);
          }
        } catch {
          if (requestId.current === thisRequest) {
            setPublicResults([]);
            setHasMore(false);
          }
        } finally {
          if (requestId.current === thisRequest) setLoading(false);
        }
      },
      searchQuery ? 300 : 0
    );
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadMore = async () => {
    if (loadingMore || loading) return;
    const thisRequest = requestId.current;
    setLoadingMore(true);
    try {
      const page = await queryClient.query({
        queryKey: queryKeys.channelSearch(searchQuery, publicResults.length),
        queryFn: () => searchChannels(searchQuery, publicResults.length, PAGE_SIZE),
      });
      const hydrated = (await Promise.all(page.data.map((n) => fetchChannel(n)))).filter(
        (c): c is Channel => c !== null
      );
      if (requestId.current === thisRequest) {
        setPublicResults((prev) => {
          const seen = new Set(prev.map((c) => c.nickname));
          return [...prev, ...hydrated.filter((c) => !seen.has(c.nickname))];
        });
        setHasMore(page.meta.pagination.hasMore);
      }
    } catch {
      if (requestId.current === thisRequest) setHasMore(false);
    } finally {
      if (requestId.current === thisRequest) setLoadingMore(false);
    }
  };

  const sentinelRef = useInfiniteScroll(loadMore, hasMore && publicResults.length > 0);

  // Tracks the live scroll offset in a ref (no re-render per scroll
  // event) so it's ready to snapshot the instant this screen unmounts -
  // by the time an unmount effect's cleanup runs, `searchQuery`/
  // `publicResults`/`hasMore` closed over at mount time could be stale,
  // so those go through refs too rather than being read directly.
  const scrollYRef = useRef(restored?.scrollY ?? 0);
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const resultsRef = useRef(publicResults);
  resultsRef.current = publicResults;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  useEffect(() => {
    const onScroll = () => {
      scrollYRef.current = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      showcaseCache = {
        query: searchQueryRef.current,
        results: resultsRef.current,
        hasMore: hasMoreRef.current,
        scrollY: scrollYRef.current,
      };
    };
  }, []);

  // Jumps back to the saved offset once, right after a restored mount.
  // rAF (rather than firing synchronously) waits for the just-restored
  // results to actually paint first, since the page needs its real
  // height before scrollTo lands anywhere meaningful.
  useEffect(() => {
    if (!restored) return;
    const raf = requestAnimationFrame(() => window.scrollTo(0, restored.scrollY));
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show every public channel the backend returns, including ones the
  // caller already owns / joined / follows - the browse list is meant to
  // surface as much as it can, and being a member doesn't mean "hide it
  // here". De-duped by nickname just in case.
  const seen = new Set<string>();
  const filtered = publicResults.filter((c) => {
    if (seen.has(c.nickname)) return false;
    seen.add(c.nickname);
    return true;
  });

  return (
    <div className="flex flex-col gap-4">
      <SectionTitle title="Showcase" subtitle="Browse public channels and topics" />
      <input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search public channels"
        className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
      />
      {!searchQuery && !loading && filtered.length > 0 && (
        <span className="text-xs font-medium uppercase tracking-wide text-black/40 dark:text-white/40">Top channels</span>
      )}
      {loading && <span className="text-sm text-black/40 dark:text-white/40 text-center">Searching...</span>}
      {!loading && filtered.length === 0 && (
        <span className="text-sm text-black/40 dark:text-white/40 text-center">
          {searchQuery ? 'No public channels match that search.' : 'No public channels to show yet.'}
        </span>
      )}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
        {/* Straight into ChatScreen, not the Details preview - ChatScreen
            already opens on the pane matching this channel's own viewType
            (messages/charts/reports - see its `viewType` const) and
            handles a public channel that isn't in the caller's own list
            yet via ensureChannelLoaded, same as Details screen's "Open
            chat" button. Browsing Showcase should land you where the
            channel's owner configured it, not on an extra preview screen
            first. */}
        {filtered.map((channel) => (
          <Link key={channel.id} to={`/channels/${channel.id}`}>
            <AppCard className="flex flex-col gap-2.5 h-full">
              <AvatarView symbol={channel.avatarSymbol} colorHex={channel.accentHex} imageKey={channel.avatarImageKey} size={54} />
              <span className="font-semibold">{channel.title}</span>
              <span className="text-xs text-black/50 dark:text-white/50">{channel.nickname}</span>
            </AppCard>
          </Link>
        ))}
      </div>

      {!loading && filtered.length > 0 && hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-black/30 dark:text-white/30" />
        </div>
      )}
    </div>
  );
}
